import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChartBox } from "@unified-checkout/design-system";
import { SkeletonLoader } from "@unified-checkout/design-system";
import { BarChart } from "@unified-checkout/design-system";
import type { ApexOptions } from "apexcharts";
import type { IHomeApisParams } from "@app/dashboard/types/innerLayout/home";
import type { ILegendItem } from "libs/design-system/src/types/charts/chart_box";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import { getTerminalsRevenue } from "@app/dashboard/services/inner_layout/home/charts";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useLangStore } from "@app/dashboard/store/lang";
import { getBarChartOptions } from "@app/dashboard/utils/charts";

const TerminalsRevenue = (
  { apiParams, daysInRange }
    :
    { apiParams: IHomeApisParams, daysInRange: number }
) => {

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { filters } = useFiltersStore();
  const { lang } = useLangStore();

  const [chartOptions, setChartOptions] = useState<ApexOptions>();

  const legendList: ILegendItem[] = [
    { color: '#144DFF', label: t('innerLayout.homePage.charts.current') },
    { color: '#D0DBFF', label: t('innerLayout.homePage.charts.previous') },
  ];

  const {
    isLoading,
    isError,
    data: response,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_TERMINALS_REVENUE, filters],
    queryFn: () => getTerminalsRevenue(apiParams),
    enabled: Object.values(filters).every(value => value !== undefined)
  });

  const chartData = response?.data;
  const mainData = chartData?.data;
  const compareData = chartData?.compare?.data;

  const mainSeries = mainData?.map((obj: any) => obj.value ?? 0) || [];
  const compareSeries = compareData?.map((obj: any) => obj.value ?? 0) || [];

  const titles = mainData?.map((obj: any) => obj.title ?? "");
  const { en_currency, ar_currency } = chartData || {};

  const currency = { en: en_currency, ar: ar_currency };

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_TERMINALS_REVENUE] });
  }, []);

  useEffect(() => {
    titles && setChartOptions(
      getBarChartOptions(
        t,
        lang,
        titles,
        currency,
        daysInRange
      )
    );
  }, [chartData, lang]);

  return (

    (
      isError ?
        <ChartBox>
          <ComponentError text={t('innerLayout.errors.unableToRetrieveData')} />
        </ChartBox>
        :
        isLoading ?
          <SkeletonLoader noOfRows={8} />
          :
          (
            response ?
              <ChartBox
                title={chartData[`${lang}_title`]}
                info={chartData[`info_${lang}`]}
                legendList={legendList}
                legendPlacement="top"
              >
                <BarChart
                  series={[
                    { data: mainSeries },
                    { data: compareSeries }
                  ]}
                  options={chartOptions}
                />
              </ChartBox>
              :
              <SkeletonLoader noOfRows={8} />
          )
    )
  )
}

export default TerminalsRevenue;